import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { paths } from "@src/packages/openapi/schema";
import type { RequestBody, ResponseBody } from "@src/packages/openapi/helpers";

// The generated client is typed from the unversioned default OpenAPI
// document; this hook pins x-api-version 2026-08-27, whose responses are
// settings-only. Narrow to that contract so dropped persistence metadata
// (id, userId, timestamps) cannot be read.
const apiVersionHeaders = { "x-api-version": "2026-08-27" } as const;

export type Preferences = Pick<
  ResponseBody<paths["/me/preferences"]["get"]>["data"],
  | "language"
  | "timezone"
  | "timezoneLocation"
  | "timeFormat"
  | "dateFormat"
  | "soundEnabled"
  | "hapticsEnabled"
>;
export type UpdatePreferencesPayload = RequestBody<
  paths["/me/preferences"]["patch"]
>;

function preferencesQueryKey(patientId: string | undefined) {
  // Preferences are personal data. Keep their cache entry scoped to the
  // authenticated patient so a sign-out/sign-in transition cannot briefly
  // render a previous patient's locale or feedback settings.
  return ["preferences", patientId ?? "unauthenticated"] as const;
}

export function usePreferences() {
  const { client, patientId } = useApiClient();
  const queryKey = preferencesQueryKey(patientId);

  return useQuery<Preferences>({
    queryKey,
    enabled: Boolean(patientId),
    queryFn: async () => {
      const { data, error, response } = await client.GET("/me/preferences", {
        headers: apiVersionHeaders,
      });
      if (error) {
        throwApiError(
          error,
          "We couldn't load your preferences. Please try again.",
          "usePreferences",
          response?.status,
        );
      }
      return data.data;
    },
  });
}

export function useUpdatePreferences() {
  const { client, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const queryKey = preferencesQueryKey(patientId);

  return useMutation({
    mutationFn: async (payload: UpdatePreferencesPayload) => {
      const { data, error, response } = await client.PATCH("/me/preferences", {
        body: payload,
        headers: apiVersionHeaders,
      });
      if (error) {
        throwApiError(
          error,
          "We couldn't save your preferences. Please try again.",
          "useUpdatePreferences",
          response?.status,
        );
      }
      return data.data;
    },
    onMutate: async (payload: UpdatePreferencesPayload) => {
      // Prevent an in-flight read from overwriting the optimistic preference
      // while the serialized writer is sending the patient's latest choice.
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Preferences>(queryKey);
      queryClient.setQueryData<Preferences>(queryKey, (current) => {
        if (!current) return current;
        return { ...current, ...payload };
      });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSuccess: (preferences, payload) => {
      // The paired API migration returns `timezoneLocation`. Keep a selected
      // city visible during a rolling deploy too: an older API response simply
      // omits the new field, even though it accepted the rest of the patch.
      // Once the API is updated, its explicit string or null is authoritative.
      const hasTimezoneLocation = Object.prototype.hasOwnProperty.call(
        preferences,
        "timezoneLocation",
      );

      queryClient.setQueryData<Preferences>(queryKey, (current) => {
        if (hasTimezoneLocation) return preferences;

        const payloadIncludesLocation = Object.prototype.hasOwnProperty.call(
          payload,
          "timezoneLocation",
        );

        return {
          ...preferences,
          timezoneLocation: payloadIncludesLocation
            ? (payload.timezoneLocation ?? null)
            : (current?.timezoneLocation ?? null),
        };
      });
    },
  });
}
